// AccountScreen — self-serve profile editor for DropPin subscribers.
//
// v0.45.25 — initial. Brett: "Dont understand how the subscriber will
// change their details in admin? They dont have access." The UserMenu
// previously redirected users to InSpec Admin, which subscribers can't
// reach. This screen lets them update their own row in TenantUsers.
//
// Route: /account (registered in App.jsx)
//
// Editable: First name, Last name, Phone, Company.
// Read-only here:
//   * Email — change requires a verification email (follow-up rev).
//   * Role  — assigned by the tenant owner.
//   * Password — handled at /reset-password via the forgot-flow.

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { theme, card, input, btnPrimary, btnSecondary } from '../theme';
import SeatLicensingPanel from '../components/SeatLicensingPanel';
import MoveTeamsPanel from '../components/MoveTeamsPanel';
// v0.46.7 — Shared page title (standardised heading size).
import PageHeading from '../components/PageHeading';
import { ArrowLeft, Mail, ShieldCheck, KeyRound, Trash2 } from 'lucide-react';
import { getCurrentUser, setCurrentUser, clearCurrentUser } from '../utils/user';
import { apiGet, apiSend } from '../api';

export default function AccountScreen() {
  const navigate = useNavigate();
  const session  = getCurrentUser();
  const userId   = session?.Id || session?.id;

  const [loading, setLoading] = useState(true);
  const [saving,  setSaving]  = useState(false);
  const [error,   setError]   = useState(null);
  const [saved,   setSaved]   = useState(false);

  const [firstName, setFirstName] = useState('');
  const [lastName,  setLastName]  = useState('');
  const [phone,     setPhone]     = useState('');
  const [company,   setCompany]   = useState('');
  const [email,     setEmail]     = useState('');
  const [role,      setRole]      = useState('');
  const [verified,  setVerified]  = useState(false);

  // Load fresh server-side state on mount — the localStorage copy can
  // be stale (e.g. after an admin role change). Falls back to the
  // session payload if /api/account isn't reachable.
  useEffect(() => {
    if (!userId) { setLoading(false); return; }
    let cancelled = false;
    apiGet(`/api/account?id=${encodeURIComponent(userId)}`)
      .then(u => {
        if (cancelled || !u) return;
        setFirstName(u.FirstName || '');
        setLastName(u.LastName   || '');
        setPhone(u.Phone         || '');
        setCompany(u.Company     || '');
        setEmail(u.Email         || '');
        setRole(u.Role           || '');
        setVerified(!!u.EmailVerified);
      })
      .catch(e => {
        // Fall back to the localStorage payload so the form is at
        // least pre-filled, even if the server is down.
        setError(e?.message || 'Could not load your account from the server. Showing your last cached values — saving will retry.');
        const s = session || {};
        setFirstName(s.FirstName || s.firstName || '');
        setLastName(s.LastName   || s.lastName  || '');
        setPhone(s.Phone         || s.phone     || '');
        setCompany(s.Company     || s.company   || '');
        setEmail(s.Email         || s.email     || '');
        setRole(s.Role           || s.role      || '');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  async function save(e) {
    e?.preventDefault?.();
    if (!userId) {
      setError('No active session.');
      return;
    }
    if (!firstName.trim()) {
      setError('First name is required.');
      return;
    }
    setSaving(true); setError(null); setSaved(false);
    try {
      const updated = await apiSend('PATCH', `/api/account?id=${encodeURIComponent(userId)}`, {
        firstName: firstName.trim(),
        lastName:  lastName.trim(),
        phone:     phone.trim(),
        company:   company.trim(),
      });
      // Merge the new fields into the current session so UserMenu +
      // anything else reading getCurrentUser() picks them up without
      // a reload.
      const merged = { ...(session || {}), ...updated };
      setCurrentUser(merged);
      setSaved(true);
    } catch (err) {
      setError(err?.message || String(err));
    } finally {
      setSaving(false);
    }
  }

  if (!userId) {
    return (
      <div style={{ maxWidth: 600, margin: '24px auto', padding: 16 }}>
        <div style={{ ...card, padding: 20 }}>
          <h2 style={{ margin: '0 0 8px', fontSize: 18 }}>Not signed in</h2>
          <div style={{ fontSize: 13, color: theme.textLight }}>
            Open the sign-in page to access your account.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 720, margin: '0 auto', padding: '8px 4px 32px' }}>
      <button
        type="button"
        onClick={() => navigate(-1)}
        style={{
          background: 'transparent', border: 'none',
          color: theme.textLight, fontSize: 13, cursor: 'pointer',
          display: 'inline-flex', alignItems: 'center', gap: 4,
          padding: 0, marginBottom: 10, fontFamily: 'inherit',
        }}
      >
        <ArrowLeft size={14} /> Back
      </button>

      <PageHeading style={{ margin: '0 0 4px' }}>Your account</PageHeading>
      <div style={{ fontSize: 13, color: theme.textLight, marginBottom: 18 }}>
        Update your profile details. Email + role changes need a separate flow — see below.
      </div>

      {loading ? (
        <div style={{ ...card, padding: 20, color: theme.textLight, fontSize: 13 }}>Loading your details…</div>
      ) : (
        <form onSubmit={save} style={{ ...card, padding: 20 }}>
          <Section title="Profile">
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <Field label="First name" required>
                <input value={firstName} onChange={e => setFirstName(e.target.value)} style={input} autoFocus maxLength={80} />
              </Field>
              <Field label="Last name">
                <input value={lastName} onChange={e => setLastName(e.target.value)} style={input} maxLength={80} />
              </Field>
              <Field label="Phone">
                <input value={phone} onChange={e => setPhone(e.target.value)} style={input} maxLength={40} placeholder="04…" />
              </Field>
              <Field label="Company">
                <input value={company} onChange={e => setCompany(e.target.value)} style={input} maxLength={200} placeholder="e.g. InSite Building Group" />
              </Field>
            </div>
          </Section>

          <Section title="Sign-in & access" tone="muted">
            <ReadOnlyRow icon={<Mail size={14} />} label="Email" value={email}
              helper={verified ? 'Verified' : 'Not yet verified — check your inbox.'}
              action={<span style={{ fontSize: 11, color: theme.textLight }}>Change coming soon — email <a href="mailto:support@insitebg.com.au" style={{ color: theme.brand }}>support</a> to update.</span>}
            />
            <ReadOnlyRow icon={<ShieldCheck size={14} />} label="Role" value={role || '—'}
              helper="Assigned by your tenant owner."
            />
            <ReadOnlyRow icon={<KeyRound size={14} />} label="Password" value="••••••••"
              helper="Use the Forgot password link on the sign-in screen to reset it."
            />
          </Section>

          {error && (
            <div style={{
              background: theme.bg, border: `1px solid ${theme.danger}`,
              color: theme.danger, padding: '8px 10px', borderRadius: 6,
              fontSize: 12, marginTop: 12,
            }}>{error}</div>
          )}
          {saved && !error && (
            <div style={{
              background: theme.bg, border: `1px solid ${theme.success}`,
              color: theme.success, padding: '8px 10px', borderRadius: 6,
              fontSize: 12, marginTop: 12, fontWeight: 600,
            }}>Saved.</div>
          )}

          <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
            <button type="submit" disabled={saving} style={{ ...btnPrimary, opacity: saving ? 0.6 : 1 }}>
              {saving ? 'Saving…' : 'Save changes'}
            </button>
            <button type="button" onClick={() => navigate(-1)} style={btnSecondary}>Cancel</button>
          </div>
        </form>
      )}

      <SeatLicensingPanel productCode="droppin" productLabel="DropPin" />

      {/* v0.339.0 — owner/admin only (the server 403s anyone else; the panel then draws nothing). */}
      <MoveTeamsPanel />

      {/* Store-readiness: in-app account deletion (Apple 5.1.1(v) — DropPin
          has in-app signup, so deletion must also be in-app). The server
          re-checks everything; this is honest UI over an honest endpoint. */}
      <DeleteAccountPanel email={email || session?.email || ''} />
    </div>
  );
}

function DeleteAccountPanel({ email }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ ...card, padding: 20, marginTop: 18, borderColor: theme.danger }}>
      <div style={{
        fontSize: 11, fontWeight: 800, letterSpacing: '0.06em',
        textTransform: 'uppercase', color: theme.danger, marginBottom: 10,
      }}>Delete account</div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ fontSize: 13, color: theme.textLight, maxWidth: 480 }}>
          Permanently deletes your sign-in and, if you are the only member of
          your workspace, everything in it.
        </div>
        <button
          type="button"
          onClick={() => setOpen(true)}
          style={{ ...btnSecondary, color: theme.danger, borderColor: theme.danger, display: 'inline-flex', alignItems: 'center', gap: 6 }}
        >
          <Trash2 size={14} /> Delete my account…
        </button>
      </div>
      {open && <DeleteAccountModal email={email} onCancel={() => setOpen(false)} />}
    </div>
  );
}

function DeleteAccountModal({ email, onCancel }) {
  const [typed, setTyped]       = useState('');
  const [busy, setBusy]         = useState(false);
  const [error, setError]       = useState(null);
  const match = typed.trim().toLowerCase() === String(email || '').trim().toLowerCase();

  async function doDelete() {
    if (!match || busy) return;
    setBusy(true); setError(null);
    try {
      // Direct fetch on purpose: apiSend queues mutations into the offline
      // outbox, and an account deletion must never be a queued surprise.
      const r = await fetch('/api/account-delete', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ confirmEmail: typed.trim() }),
      });
      const body = await r.json().catch(() => ({}));
      if (!r.ok) { setError(body?.error || `Deletion failed (HTTP ${r.status}).`); setBusy(false); return; }
      // Gone. Clear the local cache and hard-reload to the sign-in screen.
      clearCurrentUser();
      window.location.href = '/';
    } catch (_e) {
      setError('Could not reach the server. Check your connection — nothing was deleted.');
      setBusy(false);
    }
  }

  return (
    <div
      onClick={() => { if (!busy) onCancel(); }}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 100, padding: 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{ background: theme.surface, width: 520, maxWidth: '100%', borderRadius: 10, padding: 22 }}
      >
        <h3 style={{ margin: '0 0 10px', fontSize: 18, color: theme.danger }}>Delete your account?</h3>
        <div style={{ fontSize: 13, color: theme.text, lineHeight: 1.55, marginBottom: 12 }}>
          This permanently deletes:
          <ul style={{ margin: '6px 0 0', paddingLeft: 20 }}>
            <li>your sign-in profile — name, email, phone and password;</li>
            <li>if you are the <b>only member</b> of your workspace: all its
              projects, plans, pins, photos and reports — and share links you
              have sent will stop working;</li>
          </ul>
        </div>
        <div style={{ fontSize: 12, color: theme.textLight, lineHeight: 1.55, marginBottom: 14 }}>
          Reports already sent to others remain as the recipient&rsquo;s copy.
          If your workspace has other members, its data stays with them — only
          your access and profile are removed. This cannot be undone.
        </div>
        <div style={{ fontSize: 11, fontWeight: 600, color: theme.textLight, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 5 }}>
          Type your email to confirm
        </div>
        <input
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && match && !busy) doDelete();
            if (e.key === 'Escape' && !busy) onCancel();
          }}
          style={input}
          placeholder={email}
          autoFocus
          disabled={busy}
        />
        {error && (
          <div style={{
            background: theme.bg, border: `1px solid ${theme.danger}`,
            color: theme.danger, padding: '8px 10px', borderRadius: 6,
            fontSize: 12, marginTop: 12,
          }}>{error}</div>
        )}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 18 }}>
          <button onClick={onCancel} disabled={busy} style={btnSecondary}>Cancel</button>
          <button
            onClick={doDelete}
            disabled={!match || busy}
            style={{
              ...btnPrimary, background: theme.danger,
              opacity: (!match || busy) ? 0.5 : 1,
              display: 'inline-flex', alignItems: 'center', gap: 6,
            }}
          >
            <Trash2 size={14} /> {busy ? 'Deleting…' : 'Permanently delete'}
          </button>
        </div>
      </div>
    </div>
  );
}

function Section({ title, tone, children }) {
  return (
    <div style={{
      marginBottom: 14, paddingBottom: 14,
      borderBottom: `1px solid ${theme.border}`,
    }}>
      <div style={{
        fontSize: 11, fontWeight: 800, letterSpacing: '0.06em',
        textTransform: 'uppercase',
        color: tone === 'muted' ? theme.textLight : theme.text,
        marginBottom: 10,
      }}>{title}</div>
      {children}
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <div>
      <div style={{ fontSize: 11, fontWeight: 600, color: theme.textLight, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 4 }}>
        {label}{required && <span style={{ color: theme.danger, marginLeft: 4 }}>*</span>}
      </div>
      {children}
    </div>
  );
}

function ReadOnlyRow({ icon, label, value, helper, action }) {
  return (
    <div style={{
      display: 'grid', gridTemplateColumns: '110px 1fr',
      gap: 10, padding: '8px 0',
      borderTop: `1px dashed ${theme.border}`,
      fontSize: 13,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: theme.textLight, fontWeight: 600 }}>
        {icon} {label}
      </div>
      <div>
        <div style={{ color: theme.text }}>{value}</div>
        {helper && <div style={{ fontSize: 11, color: theme.textLight, marginTop: 2 }}>{helper}</div>}
        {action && <div style={{ marginTop: 4 }}>{action}</div>}
      </div>
    </div>
  );
}
